import { authFetch } from '../../api/csrf';
import { get, post, put, del } from '../apiClient';
import { API_ENDPOINTS } from '../config';

const jobService = {
  // Get all job posts with optional filters
  getJobs: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.JOBS.LIST}?${queryString}` : API_ENDPOINTS.JOBS.LIST;
      
      const response = await get(url);
      

      if (response.responseCode === 200 || response.data) {
        return {
          success: true,
          responseCode: response.responseCode || 200,
          data: response.data || response
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch jobs'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fetch jobs'
      };
    }
  },

  // Get job by ID
  getJobById: async (id) => {
    try {
      const response = await get(API_ENDPOINTS.JOBS.GET(id));

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch job details'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fetch job details'
      };
    }
  },

  // Export a single job post's full detail as a backend-generated Excel (.xlsx)
  // file and trigger the browser download. The workbook is built entirely on the
  // server; the frontend only requests the file and saves it.
  exportJobDetail: async (id, fallbackName = 'Job') => {
    try {
      if (!id) {
        return { success: false, error: 'Job ID is required' };
      }

      const blob = await get(API_ENDPOINTS.JOBS.EXPORT_DETAIL(id), { responseType: 'blob' });

      const sanitized = String(fallbackName || 'Job')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Job';
      const filename = `Job_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export job details'
      };
    }
  },

  // Create new job post
  createJob: async (jobData) => {
    try {
      const response = await post(API_ENDPOINTS.JOBS.CREATE, jobData);

      if (response.responseCode === 201) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create job'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create job'
      };
    }
  },

  // Update job post
  updateJob: async (id, jobData) => {
    try {
      
      const response = await put(API_ENDPOINTS.JOBS.UPDATE(id), jobData);
      

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        
        return {
          success: false,
          error: response.responseMessage || 'Failed to update job'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update job'
      };
    }
  },

  // Delete job post
  deleteJob: async (id) => {
    try {
      const response = await del(API_ENDPOINTS.JOBS.DELETE(id));

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete job'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete job'
      };
    }
  },

  // Bulk delete job posts
  bulkDeleteJobs: async (ids) => {
    try {
      const response = await post(API_ENDPOINTS.JOBS.BULK_DELETE, { ids });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete jobs'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete jobs'
      };
    }
  },

  // Publish job post
  publishJob: async (id) => {
    try {
      const response = await post(API_ENDPOINTS.JOBS.PUBLISH(id));

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to publish job'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to publish job'
      };
    }
  },

  // Get job applications
  getJobApplications: async (id, queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `${API_ENDPOINTS.JOBS.APPLICATIONS(id)}?${queryString}` : API_ENDPOINTS.JOBS.APPLICATIONS(id);
      const response = await get(url);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to fetch job applications'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fetch job applications'
      };
    }
  },

  /**
   * Bulk upload job posts from parsed CSV data
   * @param {Array} jobs - Array of job objects
   * @param {Object} options - Upload options (skipDuplicates, validateOnly)
   */
  bulkUploadJobs: async (jobs, options = {}) => {
    try {
      const { skipDuplicates = true, validateOnly = false } = options;

      const response = await post(`${API_ENDPOINTS.JOBS.LIST}/bulk-upload`, {
        jobs,
        skipDuplicates,
        validateOnly
      });

      if (response && (response.responseCode === 201 || response.responseCode === 200)) {
        return {
          success: true,
          data: response.data,
          message: response.responseMessage || 'Bulk upload completed'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to upload jobs',
          data: response.data
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to upload jobs',
        data: error.response?.data?.data
      };
    }
  },

  /**
   * Validate bulk upload data without creating jobs
   * @param {Array} jobs - Array of job objects to validate
   */
  validateBulkUpload: async (jobs) => {
    try {
      const response = await post(`${API_ENDPOINTS.JOBS.LIST}/bulk-upload/validate`, {
        jobs
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
          isValid: response.data?.isValid,
          errors: response.data?.errors || [],
          duplicates: response.data?.duplicates || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to validate jobs',
          data: response.data
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || error.message || 'Failed to validate jobs'
      };
    }
  },

  /**
   * Download bulk upload CSV template
   */
  downloadBulkUploadTemplate: async (filename = 'job_bulk_upload_template.csv') => {
    try {      const response = await authFetch(`${API_ENDPOINTS.JOBS.LIST}/bulk-upload/template`, {
        credentials: 'include',
        method: 'GET',
        headers: {
                  }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to download template'
      };
    }
  },

  /**
   * Parse CSV content to job objects
   */
  parseCSV: (csvContent) => {
    try {
      const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
      if (lines.length < 2) {
        return {
          success: false,
          error: 'CSV must contain a header row and at least one data row'
        };
      }

      // Parse header
      const headers = parseCSVLine(lines[0]);

      // Parse data rows
      const jobs = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0 || values.every(v => !v.trim())) continue;

        const job = {};
        headers.forEach((header, index) => {
          const key = header.trim();
          job[key] = values[index]?.trim() || '';
        });
        jobs.push(job);
      }

      return { success: true, jobs };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to parse CSV'
      };
    }
  }
};

/**
 * Helper function to parse a CSV line handling quoted values
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current);
  return values;
};

export default jobService;
